/**
 * Iter269.20aa — Doc feedback widget.
 *
 * Renders "Was this article helpful?" with thumbs-up / thumbs-down.
 * Anonymous voting backed by an httpOnly `qt_doc_voter` cookie set
 * by the backend on first POST. Returning voters can change their
 * mind (the backend overwrites the existing row).
 *
 * Auto-mounts at the END of any `.qt-doc-content` or `.qt-legal-prose`
 * container so it ships to Methodology, Audit, all Legal pages, and
 * any future doc page with one of those wrapper classes — zero per-
 * page wiring needed.
 *
 * Idempotent: if a container already has a feedback root, skip it.
 * Re-runs on route change via MutationObserver, so SPA navigation
 * mounts the widget on each new doc page.
 */
import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { ThumbsUp, ThumbsDown, Check, Loader2, MessageSquare } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const TARGET_SELECTOR = ".qt-doc-content, .qt-legal-prose";
const ROOT_DATA_ATTR  = "data-doc-feedback-root";

/* -----------------------------------------------------------------
 * Container-mounter wrapper. Renders ONE portal per qualifying
 * doc container on the current page. Watches the DOM for new
 * containers added by SPA route changes.
 * ----------------------------------------------------------------- */
export default function DocFeedbackMounter() {
  const [hosts, setHosts] = useState([]);

  useEffect(() => {
    const refresh = () => {
      const next = [];
      document.querySelectorAll(TARGET_SELECTOR).forEach((container) => {
        let root = container.querySelector(`[${ROOT_DATA_ATTR}]`);
        if (!root) {
          root = document.createElement("div");
          root.setAttribute(ROOT_DATA_ATTR, "true");
          root.style.marginTop = "48px";
          container.appendChild(root);
        }
        // De-dup: only include each host once.
        if (!next.find((n) => n === root)) next.push(root);
      });
      setHosts((prev) => {
        // Avoid useless re-renders if the host list is unchanged.
        if (prev.length === next.length && prev.every((p, i) => p === next[i])) return prev;
        return next;
      });
    };

    refresh();
    const mo = new MutationObserver(() => refresh());
    mo.observe(document.body, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, []);

  return (
    <>
      {hosts.map((host, i) => (
        <DocFeedbackPortal key={i} host={host} />
      ))}
    </>
  );
}

function DocFeedbackPortal({ host }) {
  const [docPath] = useState(() => normalizePath(window.location.pathname));
  return createPortal(<DocFeedbackWidget docPath={docPath} />, host);
}

function normalizePath(p) {
  let path = (p || "/").split("#")[0].split("?")[0];
  if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
  return path.toLowerCase();
}

/* -----------------------------------------------------------------
 * Widget UI
 * ----------------------------------------------------------------- */
function DocFeedbackWidget({ docPath }) {
  const [loading, setLoading]   = useState(true);
  const [summary, setSummary]   = useState(null);
  const [myVote,  setMyVote]    = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [comment, setComment]   = useState("");
  const [thanks,  setThanks]    = useState(false);
  const [error,   setError]     = useState(null);

  // Fetch existing vote on mount
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/public/doc-feedback/summary?path=${encodeURIComponent(docPath)}`, {
      withCredentials: true,
    })
      .then((r) => {
        if (!alive) return;
        setSummary(r.data);
        if (r.data?.my_vote) {
          setMyVote(r.data.my_vote);
          setThanks(true);  // already voted — show thanks state immediately
        }
      })
      .catch(() => { /* widget is non-critical; fail silent */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [docPath]);

  const submitVote = useCallback(async (vote) => {
    setError(null);
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/api/public/doc-feedback`,
        { doc_path: docPath, vote },
        { withCredentials: true },
      );
      setMyVote(vote);
      setThanks(true);
      setShowCommentBox(true);
      // Bump the local count immediately for snappier UX.
      setSummary((s) => {
        if (!s) return { doc_path: docPath, up: vote === "up" ? 1 : 0, down: vote === "down" ? 1 : 0, total: 1 };
        // If user is switching their vote, decrement the other bucket.
        const wasUp   = myVote === "up";
        const wasDown = myVote === "down";
        return {
          ...s,
          up:    s.up   + (vote === "up"   ? 1 : 0) - (wasUp   ? 1 : 0),
          down:  s.down + (vote === "down" ? 1 : 0) - (wasDown ? 1 : 0),
          total: myVote ? s.total : s.total + 1,
        };
      });
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't submit feedback. Try again.");
    } finally { setSubmitting(false); }
  }, [docPath, myVote]);

  const submitComment = useCallback(async () => {
    if (!comment.trim() || !myVote) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/api/public/doc-feedback`,
        { doc_path: docPath, vote: myVote, comment: comment.trim() },
        { withCredentials: true },
      );
      setShowCommentBox(false);
      setComment("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't submit comment.");
    } finally { setSubmitting(false); }
  }, [docPath, myVote, comment]);

  if (loading) return null;   // don't flash an empty widget before summary arrives

  const total      = summary?.total ?? 0;
  const helpfulPct = total > 0 ? Math.round((summary.up / total) * 100) : null;

  return (
    <section
      data-testid="doc-feedback-widget"
      data-doc-path={docPath}
      data-toc-ignore="true"
      style={{
        marginTop:    0,
        padding:      "20px 22px",
        borderRadius: 10,
        border:       "1px solid rgba(255,255,255,0.06)",
        background:   "linear-gradient(180deg, rgba(0,229,255,0.04) 0%, rgba(11,19,36,0.6) 100%)",
      }}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div
            className="qt-mono text-[10px] uppercase"
            style={{ color: "var(--qt-text-3, #94A3B8)", letterSpacing: "0.18em" }}
          >
            Article Feedback
          </div>
          <h3
            className="font-bold mt-1"
            style={{
              color: "#FFFFFF",
              fontFamily: "Chivo, sans-serif",
              fontSize: 18,
              letterSpacing: "-0.01em",
            }}
            data-testid="doc-feedback-title"
          >
            {thanks ? "Thanks for the feedback!" : "Was this article helpful?"}
          </h3>
          {total > 0 && (
            <div className="qt-mono text-[11px] mt-1" style={{ color: "var(--qt-text-3, #94A3B8)" }}>
              {total} {total === 1 ? "vote" : "votes"} · {helpfulPct ?? 0}% found this helpful
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <FeedbackButton
            kind="up"
            active={myVote === "up"}
            disabled={submitting}
            onClick={() => submitVote("up")}
            testid="doc-feedback-up"
          />
          <FeedbackButton
            kind="down"
            active={myVote === "down"}
            disabled={submitting}
            onClick={() => submitVote("down")}
            testid="doc-feedback-down"
          />
        </div>
      </div>

      {/* Comment textarea (post-vote, opt-in) */}
      {thanks && !showCommentBox && myVote && (
        <button
          type="button"
          onClick={() => setShowCommentBox(true)}
          data-testid="doc-feedback-show-comment"
          className="qt-mono text-[10px] mt-3 inline-flex items-center gap-1 uppercase tracking-widest"
          style={{
            color: "var(--qt-cyan, #00E5FF)",
            letterSpacing: "0.12em",
            background: "transparent",
            border: "none",
            padding: 0,
            cursor: "pointer",
          }}
        >
          <MessageSquare size={11} /> {myVote === "down" ? "Tell us what didn't work" : "Tell us what landed"}
        </button>
      )}

      {showCommentBox && (
        <div className="mt-3 space-y-2" data-testid="doc-feedback-comment-form">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            maxLength={800}
            placeholder={myVote === "down"
              ? "What was confusing or missing? (optional — we read every one)"
              : "What worked well? (optional)"}
            className="w-full px-3 py-2 rounded text-sm"
            style={{
              background: "rgba(4,9,20,0.7)",
              border: "1px solid rgba(255,255,255,0.10)",
              color: "#FFFFFF",
              fontFamily: "inherit",
              resize: "vertical",
              minHeight: 72,
            }}
            data-testid="doc-feedback-comment-input"
          />
          <div className="flex items-center justify-between gap-2">
            <div className="qt-mono text-[10px]" style={{ color: "var(--qt-text-3, #94A3B8)" }}>
              {comment.length} / 800
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => { setShowCommentBox(false); setComment(""); }}
                className="qt-mono text-[10px] px-3 py-1.5 rounded uppercase tracking-widest"
                style={{
                  border: "1px solid rgba(255,255,255,0.10)",
                  background: "transparent",
                  color: "#94A3B8",
                  letterSpacing: "0.12em",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitComment}
                disabled={!comment.trim() || submitting}
                data-testid="doc-feedback-comment-submit"
                className="qt-mono text-[10px] px-3 py-1.5 rounded uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                style={{
                  background: "var(--qt-cyan, #00E5FF)",
                  color: "#0B1324",
                  letterSpacing: "0.12em",
                  fontWeight: 700,
                  cursor: "pointer",
                  border: "none",
                }}
              >
                {submitting ? <><Loader2 size={11} className="animate-spin"/> Sending…</> : <>Send</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="text-xs text-red-400 mt-2" data-testid="doc-feedback-error">{error}</div>
      )}
    </section>
  );
}

function FeedbackButton({ kind, active, disabled, onClick, testid }) {
  const Icon = kind === "up" ? ThumbsUp : ThumbsDown;
  const accent = kind === "up" ? "#1FDD83" : "#FF9E6E";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      data-active={active ? "true" : "false"}
      aria-label={kind === "up" ? "Mark this article as helpful" : "Mark this article as not helpful"}
      style={{
        width: 42,
        height: 42,
        borderRadius: 10,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: active ? `${accent}22` : "rgba(11,19,36,0.6)",
        border: `1px solid ${active ? accent : "rgba(255,255,255,0.12)"}`,
        color: active ? accent : "#CBD5E1",
        cursor: disabled ? "wait" : "pointer",
        opacity: disabled ? 0.7 : 1,
        transition: "background 140ms ease, border-color 140ms ease, color 140ms ease",
      }}
    >
      {active ? <Check size={16} /> : <Icon size={16} />}
    </button>
  );
}
